import { useRef, useState } from "react";
import { nowIso } from "../constants";
import type { AppSettings, MediaRecord } from "../types/app";
import { validateMediaFile } from "../utils/fileValidation";
import { createId } from "../utils/id";
import { blobToArrayBuffer } from "../utils/blob";

interface Props {
  settings: AppSettings;
  media: MediaRecord[];
  onSettings: (patch: Partial<AppSettings>) => Promise<void>;
  onMedia: (media: MediaRecord) => Promise<void>;
  onError: (message: string) => void;
}

export function CharacterPage({ settings, media, onSettings, onMedia, onError }: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const processingKeyRef = useRef("");
  const [pendingFilename, setPendingFilename] = useState("");
  async function handleMediaFile(file: File | undefined) {
    try {
      if (!file) return;
      const processingKey = `${file.name}:${file.size}:${file.lastModified}`;
      if (processingKeyRef.current === processingKey) return;
      processingKeyRef.current = processingKey;
      setPendingFilename(file.name);
      const type = file.type.startsWith("video/") ? "video" : "image";
      if (type === "video" && file.size > 50 * 1024 * 1024) onError("大きい動画です。保存前に端末容量を確認してください。");
      const valid = await validateMediaFile(file, type);
      if (!valid.ok) {
        processingKeyRef.current = "";
        onError(valid.reason);
        return;
      }
      const id = createId("media");
      const blob = new Blob([await blobToArrayBuffer(file)], { type: file.type });
      await onMedia({ id, type, filename: file.name, mimeType: file.type, blob, state: "idle", createdAt: nowIso() });
      await onSettings({ selectedCharacterMediaId: id });
    } catch {
      processingKeyRef.current = "";
      onError("IndexedDB保存失敗です。端末の空き容量やブラウザ設定を確認してください。");
    }
  }

  return (
    <main className="screen">
      <h1>キャラクター設定</h1>
      <section className="panel">
        <label>キャラクター名<input value={settings.characterName} onChange={(event) => void onSettings({ characterName: event.target.value })} /></label>
        <label>ユーザーの呼び方<input value={settings.userCallName} onChange={(event) => void onSettings({ userCallName: event.target.value })} /></label>
        <label>性格・会話スタイル<textarea value={settings.conversationStyle} onChange={(event) => void onSettings({ conversationStyle: event.target.value })} /></label>
        <label>システムプロンプト<textarea className="prompt-box" value={settings.systemPrompt} onChange={(event) => void onSettings({ systemPrompt: event.target.value })} /></label>
        <label>静止画・動画
          <input
            ref={fileInputRef}
            data-testid="character-media-input"
            type="file"
            accept="image/jpeg,image/png,image/webp,video/mp4,video/webm"
            onChange={(event) => void handleMediaFile(event.currentTarget.files?.[0])}
            onInput={(event) => void handleMediaFile(event.currentTarget.files?.[0])}
          />
        </label>
        <button data-testid="character-media-save" type="button" onClick={() => void handleMediaFile(fileInputRef.current?.files?.[0])}>
          選択ファイルを保存
        </button>
        <select value={settings.selectedCharacterMediaId ?? ""} onChange={(event) => void onSettings({ selectedCharacterMediaId: event.target.value })}>
          <option value="">未選択</option>
          {media.filter((item) => item.type === "image" || item.type === "video").map((item) => <option key={item.id} value={item.id}>{item.filename}</option>)}
        </select>
        <div className="media-list" data-testid="media-list" aria-label="登録済みメディア">
          {pendingFilename ? <span>{pendingFilename}</span> : null}
          {media.filter((item) => item.type === "image" || item.type === "video").map((item) => <span key={item.id}>{item.filename}</span>)}
        </div>
      </section>
    </main>
  );
}
